<template>
    <div class="my-4 mx-6">
        <div v-if="isHistoryView" class="back-row" @click="$emit('back')">
            <i class="pi pi-arrow-left mr-2"></i>
            <span>Back to history</span>
        </div>

        <div class="completion-wrapper">
            <div class="flex flex-row w-full items-center">
                <i class="pi pi-calendar"></i>
                <div class="ml-12">
                    <p class="font-semibold section-muted">
                        {{ isHistoryView ? 'Past Record' : "Today's Date" }}
                    </p>
                    <p class="text-2xl font-bold">{{ displayDate }}</p>
                </div>
            </div>

            <div class="completion-card">
                <div class="completion-left">
                    <p class="title">Morning Shift</p>
                    <div class="count-row">
                        <span class="done">{{ dayCompletedCount }}</span>
                        <span class="total">/ {{ total }}</span>
                    </div>
                    <p class="subtitle">Jetties completed</p>
                </div>

                <div class="divider"></div>

                <div class="completion-right">
                    <div class="progress-circle day">
                        <svg viewBox="0 0 36 36">
                            <circle class="track" cx="18" cy="18" r="16"></circle>
                            <circle class="ring day-ring" cx="18" cy="18" r="16" :stroke-dasharray="circumference"
                                :stroke-dashoffset="dayOffset"></circle>
                        </svg>
                        <div class="progress-inner">
                            {{ dayPercentage }}%
                        </div>
                    </div>
                </div>
            </div>

            <div class="night-completion-card">
                <div class="night-completion-left">
                    <p class="night-title">Night Shift</p>
                    <div class="night-count-row">
                        <span class="night-done">{{ nightCompletedCount }}</span>
                        <span class="night-total">/ {{ total }}</span>
                    </div>
                    <p class="night-subtitle">Jetties completed</p>
                </div>

                <div class="night-divider"></div>

                <div class="night-completion-right">
                    <div class="progress-circle night">
                        <svg viewBox="0 0 36 36">
                            <circle class="track night-track" cx="18" cy="18" r="16"></circle>
                            <circle class="ring night-ring" cx="18" cy="18" r="16" :stroke-dasharray="circumference"
                                :stroke-dashoffset="nightOffset"></circle>
                        </svg>
                        <div class="progress-inner night-inner">
                            {{ nightPercentage }}%
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="signature-page">
            <div class="signature-grid">
                <ShiftSignatureCard title="Day Shift Signature" role="Team Leader" theme="day"
                    :sign-name="daySignature.signName" :sign-date="daySignature.signDate"
                    :sign-picture="daySignature.signPicture" :view-only="signing" :in-time-range="isDayShiftRange"
                    :all-checklist-completed="canSignDay"
                    :warning-text="`Complete all Day shift checklists before signing (${dayCompletedCount}/15 done)`"
                    @sign="handleSign" />

                <ShiftSignatureCard title="Night Shift Signature" role="Team Leader" theme="night"
                    :sign-name="nightSignature.signName" :sign-date="nightSignature.signDate"
                    :sign-picture="nightSignature.signPicture" :view-only="signing" :in-time-range="isNightShiftRange"
                    :all-checklist-completed="canSignNight"
                    :warning-text="`Complete all Night shift checklists before signing (${nightCompletedCount}/15 done)`"
                    @sign="handleSign" />
            </div>
        </div>

        <div class="my-8">
            <div class="flex flex-row justify-between mt-4 items-center">
                <h1 class="text-lg font-bold">All Jetties ({{ total }})</h1>

                <div v-if="isHistoryView" class="view-only-badge">
                    <i class="pi pi-lock mr-2"></i>
                    Past records are view-only
                </div>

                <div v-else>
                    <div class="dot" style="background-color: #1db879;"></div>
                    <span class="mr-4 text-sm font-semibold legend-text">Done</span>

                    <div class="dot" style="background-color: grey;"></div>
                    <span class="text-sm font-semibold legend-text">Pending</span>
                </div>
            </div>

            <div class="grid grid-cols-3 gap-4 my-6">
                <JettyCard v-for="(jetty, index) in jettyDayList" :key="jetty.jettyNo" :index="index"
                    :jettyNo="jetty.jettyNo" :dayStatus="jetty.dayStatus" :nightStatus="jetty.nightStatus"
                    :checkDate="jetty.checkDate" :viewOnly="viewOnly" :dayEntries="jetty.dayEntries"
                    :nightEntries="jetty.nightEntries" :dayCompletedCount="jetty.dayCompletedCount"
                    :nightCompletedCount="jetty.nightCompletedCount" />
            </div>
        </div>
    </div>
</template>

<script>
import 'primeicons/primeicons.css'
import ShiftSignatureCard from './ShiftSignatureCard.vue'
import JettyCard from './JettyCard.vue'
import { getJettyCheckListStatuses, signChecklist } from '@/services/user'
import { singleleUploadFile } from '@/services';
import { mapState } from 'vuex';

export default {
    name: 'ChecklistDailyView',
    components: {
        ShiftSignatureCard,
        JettyCard
    },
    props: {
        selectedDate: {
            type: String,
            default: ''
        },
        viewOnly: {
            type: Boolean,
            default: false
        },
        isHistoryView: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            todayDate: '',
            total: 15,
            activeShift: 'day',
            daySignature: {
                signName: '',
                signDate: '',
                signPicture: ''
            },
            nightSignature: {
                signName: '',
                signDate: '',
                signPicture: ''
            },
            jettyDayList: [],
            signing: false,
        }
    },
    computed: {
        ...mapState(["userInfo"]),
        dayCompletedCount() {
            return this.jettyDayList.filter(j => j.dayStatus === 1).length
        },

        nightCompletedCount() {
            return this.jettyDayList.filter(j => j.nightStatus === 1).length
        },

        dayPercentage() {
            if (!this.total) return 0
            return Math.round((this.dayCompletedCount / this.total) * 100)
        },

        nightPercentage() {
            if (!this.total) return 0
            return Math.round((this.nightCompletedCount / this.total) * 100)
        },

        circumference() {
            return 2 * Math.PI * 16
        },

        dayOffset() {
            return this.circumference * (1 - this.dayPercentage / 100)
        },

        nightOffset() {
            return this.circumference * (1 - this.nightPercentage / 100)
        },

        canSignDay() {
            return this.dayCompletedCount === 15
        },

        canSignNight() {
            return this.nightCompletedCount === 15
        },

        effectiveDate() {
            if (this.selectedDate) return this.selectedDate
            return new Date().toISOString().split('T')[0]
        },

        displayDate() {
            const date = new Date(this.effectiveDate)
            return date.toLocaleDateString('en-GB', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            })
        },

        currentSeconds() {
            const now = new Date()
            return now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds()
        },

        isDayShiftRange() {
            const start = 8 * 3600
            const end = 19 * 3600 + 59 * 60 + 59
            return this.currentSeconds >= start && this.currentSeconds <= end
        },

        isNightShiftRange() {
            const start = 20 * 3600
            const end = 7 * 3600 + 59 * 60 + 59
            return this.currentSeconds >= start || this.currentSeconds <= end
        },

        isLeader() {
            return this.$store.state.userInfo.role !== '3'
        }
    },
    watch: {
        '$route.query.refresh'() {
            this.getData()
        },
        selectedDate: {
            immediate: true,
            handler() {
                this.getData()
            }
        }
    },
    methods: {
        async getData() {
            const dateStr = this.effectiveDate
            const dateObj = new Date(dateStr)

            this.todayDate = dateObj.toLocaleDateString('en-GB', {
                weekday: 'long',
                day: 'numeric',
                month: 'short',
                year: 'numeric'
            })

            const res = await getJettyCheckListStatuses({ date: dateStr })

            const payload = res.data || {}
            const dailyStatus = payload.dailyStatus || {}
            const dayByJetty = payload.day || {}
            const nightByJetty = payload.night || {}

            this.daySignature = {
                signName: dailyStatus?.daySignature?.signBy || "",
                signDate: this.formatSignDate(dailyStatus?.daySignature?.signTime) || '',
                signPicture: dailyStatus?.daySignature?.signUrl || ''
            }

            this.nightSignature = {
                signName: dailyStatus?.nightSignature?.signBy || "",
                signDate: this.formatSignDate(dailyStatus?.nightSignature?.signTime) || '',
                signPicture: dailyStatus?.nightSignature?.signUrl || ''
            }

            const defaultJetties = [1, 2, 3, 5, 6, 7, 8, 9, 10, 11, 12, 13, 15, 16, 17]

            const previousDate = this.getPreviousDate(dateStr)

            this.jettyDayList = defaultJetties.map(jettyNo => {
                const dayEntries = dayByJetty[String(jettyNo)] || []
                const nightEntries = nightByJetty[String(jettyNo)] || []

                const firstDay = dayEntries[0] || null
                const firstNight = nightEntries[0] || null

                return {
                    jettyNo,
                    checkDate: firstDay?.checkDate || firstNight?.checkDate || dateStr,

                    dayEntries,
                    nightEntries,

                    dayStatus: dayEntries.length && dayEntries.every(e => e.status === 1) ? 1 : 0,
                    nightStatus: nightEntries.length && nightEntries.every(e => e.status === 1) ? 1 : 0,

                    previousDayLastEntry: this.getLastEntry(dayEntries),
                    previousNightLastEntry: this.getLastEntry(nightEntries),

                    previousDate
                }
            })

            this.dayCompleteNo = this.jettyDayList.filter(jetty => {
                return jetty.dayStatus === 1
            }).length

            this.nightCompleteNo = this.jettyDayList.filter(jetty => {
                return jetty.nightStatus === 1
            }).length

            this.$emit('update-completed', this.dayCompleteNo)
        },

        async handleSign(payload) {
            if (this.signing) return

            try {

                if (!payload.signPicture) {
                    this.$message && this.$message.error('Signature image missing')
                    return
                }

                const shiftType = payload.theme === 'day' ? 'DAY' : 'NIGHT'

                const file = this.dataURLToFile(
                    payload.signPicture,
                    `signature-${shiftType.toLowerCase()}-${this.effectiveDate}.png`
                )

                const uploadRes = await singleleUploadFile(file)
                const signUrl = uploadRes && uploadRes.url

                if (!signUrl) {
                    this.$message && this.$message.error('Failed to upload signature')
                    return
                }

                await signChecklist({
                    checkDate: this.effectiveDate,
                    shiftType: shiftType,
                    signUrl: signUrl
                })

                if (payload.theme === 'day') {
                    this.daySignature = {
                        signName: payload.signName || '',
                        signDate: payload.signDate,
                        signPicture: signUrl
                    }
                } else {
                    this.nightSignature = {
                        signName: payload.signName || '',
                        signDate: payload.signDate,
                        signPicture: signUrl
                    }
                }

                this.$message && this.$message.success('Signature submitted successfully')
                await this.getData()
            } catch (error) {
                console.error('signChecklist error:', error)
                this.$message && this.$message.error('Failed to submit signature')
            } finally {
                this.signing = false
            }
        },
        dataURLToBlob(dataURL) {
            const arr = dataURL.split(',')
            const mimeMatch = arr[0].match(/:(.*?);/)
            const mime = mimeMatch ? mimeMatch[1] : 'image/png'
            const bstr = atob(arr[1])
            let n = bstr.length
            const u8arr = new Uint8Array(n)

            while (n--) {
                u8arr[n] = bstr.charCodeAt(n)
            }

            return new Blob([u8arr], { type: mime })
        },

        dataURLToFile(dataURL, filename) {
            const blob = this.dataURLToBlob(dataURL)
            return new File([blob], filename, { type: blob.type || 'image/png' })
        },

        formatSignDate(dateStr) {
            if (!dateStr) return ''

            const date = new Date(dateStr)

            if (Number.isNaN(date.getTime())) return dateStr

            const hours = String(date.getHours()).padStart(2, '0')
            const minutes = String(date.getMinutes()).padStart(2, '0')

            return `${hours}:${minutes}`
        },

        getPreviousDate(dateStr) {
            const date = new Date(dateStr)
            date.setDate(date.getDate() - 1)
            return date.toISOString().split('T')[0]
        },

        getLastEntry(entries) {
            if (!Array.isArray(entries) || !entries.length) return null
            return entries[entries.length - 1]
        },
    }
}
</script>

<<style lang="scss">
.back-row {
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    color: #6b7a90;
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 12px;
}

.section-muted {
    color: #99a7bb;
    font-size: 11px;
    margin-bottom: 2px;
}

.legend-text {
    color: #738296;
    font-size: 12px;
}

.view-only-badge {
    display: inline-flex;
    align-items: center;
    height: 32px;
    padding: 0 12px;
    border-radius: 10px;
    border: 1px solid #f0b63e;
    background: #fff7e9;
    color: #c86508;
    font-size: 12px;
    font-weight: 700;
}

.completion-wrapper {
    padding: 14px 16px;
    width: 100%;
    margin: 10px 0 14px;
    background-color: white;
    min-height: 120px;
    border-radius: 12px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 16px;

    .pi-calendar {
        transform: scale(1.8);
        padding: 4px;
        color: #008cdb;
        background-color: #eff6ff;
        border-radius: 4px;
        margin-left: 12px;
        height: 18px;
        width: 18px;
        align-self: center;
    }
}

.completion-card {
    width: 220px;
    height: 100px;
    background: #fffbea;
    border: 1px solid #fbddba;
    border-radius: 18px;
    box-shadow: 0 3px 8px rgba(31, 41, 55, 0.06);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px;
    box-sizing: border-box;
    flex-shrink: 0;
}

.completion-left {
    flex: 1;
    width: 130px;
}

.title {
    margin: 0 0 6px;
    font-size: 12px;
    font-weight: 600;
    color: #f64900;
}

.count-row {
    display: flex;
    align-items: baseline;
    gap: 6px;
    margin-bottom: 8px;
}

.done {
    font-size: 22px;
    line-height: 1;
    font-weight: 800;
    color: #f64900;
}

.total {
    font-size: 13px;
    line-height: 1;
    font-weight: 700;
    color: #8f9db3;
}

.subtitle {
    margin: 0;
    font-size: 10px;
    font-weight: 600;
    color: #ff8905;
}

.divider {
    width: 1px;
    height: 64px;
    background: #ffe1bd;
    margin: 0 12px;
    border-radius: 2px;
}

.completion-right,
.night-completion-right {
    width: 56px;
    display: flex;
    justify-content: center;
    align-items: center;
}

.progress-circle {
    width: 56px;
    height: 56px;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.progress-circle svg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    transform: rotate(-90deg);
}

.progress-circle .track,
.progress-circle .ring {
    fill: none;
    stroke-width: 4;
}

.progress-circle .track {
    stroke: #fed8aa;
}

.progress-circle .ring {
    stroke-linecap: round;
    transition: stroke-dashoffset 0.3s ease;
}

.progress-circle.day .ring {
    stroke: #ea580b;
}

.progress-circle.night .track {
    stroke: #c0dbfe;
}

.progress-circle.night .ring {
    stroke: #4f39f6;
}

.progress-inner {
    width: 42px;
    height: 42px;
    border-radius: 50%;
    background: #fffaec;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 800;
    color: #1f2430;
    z-index: 1;
}

.progress-circle.night .progress-inner {
    background: #eef3ff;
}

.night-completion-card {
    width: 210px;
    height: 100px;
    background: #eef3ff;
    border: 1px solid #dceafc;
    border-radius: 18px;
    box-shadow: 0 3px 8px rgba(31, 41, 55, 0.06);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px;
    box-sizing: border-box;
    flex-shrink: 0;
}

.night-completion-left {
    flex: 1;
    width: 130px;
}

.night-title {
    margin: 0 0 6px;
    font-size: 12px;
    font-weight: 600;
    color: #165dfc;
}

.night-count-row {
    display: flex;
    align-items: baseline;
    gap: 6px;
    margin-bottom: 8px;
}

.night-done {
    font-size: 22px;
    line-height: 1;
    font-weight: 800;
    color: #2663eb;
}

.night-total {
    font-size: 13px;
    line-height: 1;
    font-weight: 700;
    color: #8f9db3;
}

.night-subtitle {
    margin: 0;
    font-size: 10px;
    font-weight: 600;
    color: #2663eb;
}

.night-divider {
    width: 1px;
    height: 64px;
    background: #c7dfff;
    margin: 0 12px;
    border-radius: 2px;
}

.signature-page {
    padding-block: 6px;
}

.signature-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
}

.dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;
    margin-right: 6px;
    align-self: center;
}

@media (max-width: 1024px) {
    .completion-wrapper {
        flex-direction: column;
        align-items: stretch;
    }

    .completion-card,
    .night-completion-card {
        width: 100%;
    }
}

@media (max-width: 768px) {
    .back-row {
        font-size: 12px;}.completion-wrapper {
    padding: 12px;
    gap: 12px;
    }

    .signature-grid {
    grid-template-columns: 1fr;
    gap: 12px;
    }

    .view-only-badge {
    height: 30px;
    padding: 0 10px;
    font-size: 11px;
    }
    }









    
</style>